import { gql } from "@apollo/client";
import { RAILS_ENV } from "helpers/constants";
import { useEffect, useState } from "react";

import useData from "./useData";

// https://docs.helpscout.com/article/1356-add-beacon-to-your-website-or-app
const BEACON_ID = "94c41fa2-4347-4054-aedb-6e83fee64358";
const BEACON_SRC = "https://beacon-v2.helpscout.net";

export default function useHelpScoutBeacon() {
  const [data] = useData(
    gql`
      query useHelpScoutBeacon {
        currentUser {
          id
          email
          fullName
        }
      }
    `,
    null,
    { skip: RAILS_ENV === "test" },
  );

  // Only load once we know who is logged in, so the beacon never shows on the login/logout pages.
  const currentUser = RAILS_ENV === "test" ? null : data?.currentUser;

  // Latched, so a refetch that momentarily empties the query does not tear the beacon down mid-load.
  const [beaconUser, beaconUserSet] = useState(null);
  useEffect(() => {
    if (currentUser) beaconUserSet(currentUser);
  }, [currentUser?.id]);

  useEffect(() => {
    if (!beaconUser) return;

    loadBeacon();
    window.Beacon("init", BEACON_ID);
    window.Beacon("config", { display: { position: "right" } });
    window.Beacon("identify", {
      email: beaconUser.email,
      // fullName is null for users with no person record, and beacon stringifies whatever it is given
      ...(beaconUser.fullName && { name: beaconUser.fullName }),
    });

    return () => {
      // Throws if it lands before the beacon has rendered, which happens when readmin unmounts quickly.
      try {
        window.Beacon("destroy");
      } catch {
        // already gone
      }
    };
  }, [beaconUser?.id]);
}

function loadBeacon() {
  if (window.Beacon) return;

  window.Beacon = function (method, options, data) {
    window.Beacon.readyQueue.push({ method, options, data });
  };
  window.Beacon.readyQueue = [];

  const script = document.createElement("script");
  script.type = "text/javascript";
  script.async = true;
  script.src = BEACON_SRC;
  document.head.appendChild(script);
}
